import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ActivityEvent {
  id: string;
  type: 'comment' | 'phase_change' | 'member_joined' | 'decision_created' | 'step_completed';
  title: string;
  description: string;
  created_at: string;
  user_name: string | null;
  user_avatar: string | null;
  metadata?: any;
}

export function useSessionActivity(sessionId: string | undefined, workspaceId: string | null) {
  const [activities, setActivities] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sessionId || !workspaceId) {
      setLoading(false);
      return;
    }

    async function fetchActivities() {
      try {
        // Fetch activity logs for this session
        const { data: logs, error: logsError } = await supabase
          .from('activity_logs')
          .select(`
            *,
            profiles:user_id (
              name,
              avatar_url
            )
          `)
          .eq('workspace_id', workspaceId)
          .eq('entity_id', sessionId)
          .order('created_at', { ascending: false });

        if (logsError) throw logsError;

        // Fetch comments for this session
        const { data: comments, error: commentsError } = await supabase
          .from('session_comments')
          .select(`
            *,
            profiles:user_id (
              name,
              avatar_url
            )
          `)
          .eq('session_id', sessionId)
          .order('created_at', { ascending: false });

        if (commentsError) throw commentsError;

        // Convert activity logs to ActivityEvent format
        const logEvents: ActivityEvent[] = (logs || []).map((log) => {
          let title = '';
          let description = '';

          switch (log.activity_type) {
            case 'decision_created':
              title = 'Entscheidung erstellt';
              description = `${log.profiles?.name || 'Jemand'} hat diese Entscheidung gestartet`;
              break;
            case 'phase_changed':
              const meta = log.metadata as any;
              title = 'Phase gewechselt';
              description = `Von Phase ${meta?.old_phase || '?'} zu Phase ${meta?.new_phase || '?'}`;
              break;
            case 'member_joined':
              title = 'Neues Team-Mitglied';
              description = `${log.profiles?.name || 'Jemand'} ist dem Workspace beigetreten`;
              break;
            default:
              title = log.activity_type;
              description = 'Aktivität';
          }

          return {
            id: log.id,
            type: log.activity_type as any,
            title,
            description,
            created_at: log.created_at,
            user_name: log.profiles?.name || null,
            user_avatar: log.profiles?.avatar_url || null,
            metadata: log.metadata,
          };
        });

        // Convert comments to ActivityEvent format
        const commentEvents: ActivityEvent[] = (comments || []).map((comment) => ({
          id: comment.id,
          type: 'comment',
          title: 'Kommentar',
          description: comment.content.substring(0, 100) + (comment.content.length > 100 ? '...' : ''),
          created_at: comment.created_at,
          user_name: comment.profiles?.name || null,
          user_avatar: comment.profiles?.avatar_url || null,
        }));

        // Merge and sort all events by created_at
        const allEvents = [...logEvents, ...commentEvents].sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );

        setActivities(allEvents);
      } catch (error) {
        console.error('Error fetching session activity:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchActivities();

    // Subscribe to realtime updates for comments
    const commentsChannel = supabase
      .channel(`session-activity-comments:${sessionId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'session_comments',
          filter: `session_id=eq.${sessionId}`,
        },
        async (payload) => {
          const { data } = await supabase
            .from('session_comments')
            .select(`
              *,
              profiles:user_id (
                name,
                avatar_url
              )
            `)
            .eq('id', payload.new.id)
            .single();

          if (data) {
            const newEvent: ActivityEvent = {
              id: data.id,
              type: 'comment',
              title: 'Kommentar',
              description: data.content.substring(0, 100) + (data.content.length > 100 ? '...' : ''),
              created_at: data.created_at,
              user_name: data.profiles?.name || null,
              user_avatar: data.profiles?.avatar_url || null,
            };
            setActivities((prev) => [newEvent, ...prev]);
          }
        }
      )
      .subscribe();

    // Subscribe to activity logs
    const logsChannel = supabase
      .channel(`session-activity-logs:${sessionId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'activity_logs',
          filter: `entity_id=eq.${sessionId}`,
        },
        async (payload) => {
          const { data } = await supabase
            .from('activity_logs')
            .select(`
              *,
              profiles:user_id (
                name,
                avatar_url
              )
            `)
            .eq('id', payload.new.id)
            .single();

          if (data) {
            let title = '';
            let description = '';

            switch (data.activity_type) {
              case 'phase_changed':
                const meta = data.metadata as any;
                title = 'Phase gewechselt';
                description = `Von Phase ${meta?.old_phase || '?'} zu Phase ${meta?.new_phase || '?'}`;
                break;
              default:
                title = data.activity_type;
                description = 'Aktivität';
            }

            const newEvent: ActivityEvent = {
              id: data.id,
              type: data.activity_type as any,
              title,
              description,
              created_at: data.created_at,
              user_name: data.profiles?.name || null,
              user_avatar: data.profiles?.avatar_url || null,
              metadata: data.metadata,
            };
            setActivities((prev) => [newEvent, ...prev]);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(commentsChannel);
      supabase.removeChannel(logsChannel);
    };
  }, [sessionId, workspaceId]);

  return {
    activities,
    loading,
  };
}
